import React, { useState, useEffect, useRef } from 'react';
import { Modal, Row, Col, Button } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import ImportPreview from './ImportPreview';

const FileModal = ({
  isOpen,
  toggle,
  title,
  uploadLabel,
  importJob,
  onImport,
  onExport,
  onDownloadTemplate,
  importLabel = "Upload your file in CSV or XLS format",
  exportLabel = "Export to Excel",
  templateLabel = "Use our template to format your file correctly",
  importHelpText = "Supports .xlsx, .xls, .csv formats",
  exportHelpText = "Exports all data in Excel format",
  templateHelpText = "Download a blank template with all column headers",
  importAccept = ".xlsx, .xls, .csv",
  importNeed = true,
  error: propError,
  loading
}) => {
  const { t } = useTranslation();
  const [importFile, setImportFile] = useState(null);
  const [localError, setLocalError] = useState('');
  const fileInputRef = useRef(null);
  
  const displayImportLabel = importLabel === "Upload your file in CSV or XLS format" ? t('shared.upload_your_file_in_csv_or_xls_format', "Upload your file in CSV or XLS format") : importLabel;
  const displayExportLabel = exportLabel === "Export to Excel" ? t('business.components.import_export_modal.export_to_excel', "Export to Excel") : exportLabel;
  const displayTemplateLabel = templateLabel === "Use our template to format your file correctly" ? t('shared.use_our_template_to_format_your_file_cor', "Use our template to format your file correctly") : templateLabel;

  useEffect(() => {
    if (!isOpen) {
      setImportFile(null);
      setLocalError('');
    }
  }, [isOpen]);

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLocalError('');
    setImportFile(file);

    if (onImport) {
      try {
        await onImport(file);
        e.target.value = '';
        setImportFile(null);
      } catch (err) {
        setLocalError(err.message || 'Failed to process import file');
        console.error('Import error:', err);
      }
    }
  };

  const handleExport = async () => {
    if (onExport) {
      try {
        await onExport();
      } catch (err) {
        setLocalError(err.message || 'Failed to export data');
        console.error('Export error:', err);
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const errorToDisplay = propError || localError;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size={importJob?.job ? "lg" : "md"}
      className="fade-scale-modal"
      backdropClassName="modal-backdrop-blur"
      centered
    >
      <div className="modal-header border-bottom">
        <h5 className="modal-title fw-semibold">
          {title || t('business.components.import_export_modal.title', 'Import/Export Assets')}
        </h5>
        <button
          type="button"
          className="btn-close"
          onClick={toggle}
          aria-label={t('shared.close', 'Close')}
        ></button>
      </div>

      <div className="modal-body py-4 px-4">
        {/* A running or previewed import takes over the dialog: showing the upload button next to a
            preview only invites a second upload on top of the first. */}
        {importJob?.job ? (
          <ImportPreview
            job={importJob.job}
            busy={importJob.busy}
            onConfirm={importJob.onConfirm}
            onCancel={importJob.onCancel}
            onClose={importJob.onClose}
            fetchAllIssues={importJob.fetchAllIssues}
            columnLabels={importJob.columnLabels}
          />
        ) : (
        <>
        {/* Step 1: Download Template */}
        {onDownloadTemplate && (
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded me-3"
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#EBF4FF',
                  color: '#3B82F6',
                  fontWeight: '600',
                  fontSize: '18px',
                  flexShrink: 0
                }}
              >
                1
              </div>
              <span className="text-dark" style={{ fontSize: '15px' }}>
                {displayTemplateLabel}
              </span>
            </div>
            <Button
              outline
              color="primary"
              onClick={onDownloadTemplate}
              disabled={loading}
              className="d-flex align-items-center justify-content-center"
              style={{ whiteSpace: 'nowrap', minWidth: '180px', flexShrink: 0 }}
            >
              <FeatherIcon icon="download" size={16} className="me-2" />
              {t('business.components.import_export_modal.download_template', 'Download Template')}
            </Button>
          </div>
        )}

        {/* Step 2: Upload File */}
        {importNeed && (
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded me-3"
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#EBF4FF',
                  color: '#3B82F6',
                  fontWeight: '600',
                  fontSize: '18px',
                  flexShrink: 0
                }}
              >
                {onDownloadTemplate ? '2' : '1'}
              </div>
              <span className="text-dark" style={{ fontSize: '15px' }}>
                {displayImportLabel}
              </span>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="d-none"
              accept={importAccept}
              onChange={handleImport}
              disabled={loading}
            />
            <Button
              color="primary"
              onClick={handleUploadClick}
              disabled={loading}
              className="d-flex align-items-center justify-content-center"
              style={{ whiteSpace: 'nowrap', minWidth: '180px', flexShrink: 0 }}
            >
              <FeatherIcon icon="file-text" size={16} className="me-2" />
              {loading
                ? t('shared.uploading', 'Uploading...')
                : (uploadLabel || t('business.components.import_export_modal.upload_assets_file', 'Upload Assets File'))}
            </Button>
          </div>
        )}

        {/* Selected file indicator */}
        {importFile && (
          <div className="mt-3 ms-5 ps-4">
            <small className="text-muted">
              <FeatherIcon icon="check-circle" size={14} className="me-1 text-success" />
              {t('shared.selected', 'Selected')}: {importFile.name}
            </small>
          </div>
        )}

        {/* Error display */}
        {errorToDisplay && (
          <div className="mt-4">
            <div className="alert alert-danger mb-0 py-2">
              {errorToDisplay}
            </div>
          </div>
        )}

        {/* Export Section - Optional, show only if onExport is provided */}
        {onExport && (
          <div className="mt-4 pt-4 border-top">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div
                  className="d-flex align-items-center justify-content-center rounded me-3"
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#F0FDF4',
                    color: '#22C55E',
                    fontWeight: '600',
                    fontSize: '18px',
                    flexShrink: 0
                  }}
                >
                  <FeatherIcon icon="download" size={20} />
                </div>
                <span className="text-dark" style={{ fontSize: '15px' }}>
                  {displayExportLabel}
                </span>
              </div>
              <Button
                outline
                color="success"
                onClick={handleExport}
                disabled={loading}
                className="d-flex align-items-center px-3"
                style={{ whiteSpace: 'nowrap' }}
              >
                <FeatherIcon icon="download" size={16} className="me-2" />
                {t('shared.tab_export_data', 'Export Data')}
              </Button>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </Modal>
  );
};

export default FileModal;
